import React, { useEffect, useState } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { HiOutlineCheckCircle, HiOutlineClock, HiOutlineXCircle } from 'react-icons/hi2';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { billingComplete, billingFromInvoicing } from '../../util/vat';
import { Modal, PrimaryButton, SecondaryButton } from '../../components';
import { BillingDetailsForm, RequestInvoicingForm } from '../../forms';
import { billingInitialValuesForUser } from '../../forms/BillingDetailsForm/BillingDetailsForm';

import css from './TransactionPanel.css';

// How each outcome of the request is presented: a status-specific title, an
// icon + colour so the result reads at a glance, and the explanatory text.
const OUTCOMES = {
  approved: {
    Icon: HiOutlineCheckCircle,
    className: css.requestInvoicingStatusApproved,
    titleId: 'RequestInvoicingModal.titleApproved',
    statusId: 'RequestInvoicingModal.statusApproved',
    textId: 'RequestInvoicingModal.resultApproved',
  },
  denied: {
    Icon: HiOutlineXCircle,
    className: css.requestInvoicingStatusDenied,
    titleId: 'RequestInvoicingModal.titleDenied',
    statusId: 'RequestInvoicingModal.statusDenied',
    textId: 'RequestInvoicingModal.resultDenied',
  },
  pending: {
    Icon: HiOutlineClock,
    className: css.requestInvoicingStatusPending,
    titleId: 'RequestInvoicingModal.titlePending',
    statusId: 'RequestInvoicingModal.statusPending',
    textId: 'RequestInvoicingModal.resultPending',
  },
};

const STEP_BILLING = 'billing';
const STEP_REQUEST = 'request';

// Invoicing is for companies: a missing or consumer profile goes through the
// billing step first (spec §5). The page has its own section 1 for this.
export const needsBillingStep = billing =>
  !billingComplete(billing) || billing.customerType !== 'BUSINESS';

const RequestInvoicingModal = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    intl,
    currentUser,
    onLookupCompany,
    lookupInProgress,
    lookupResult,
    lookupError,
    onSubmit,
    submitInProgress,
    submitError,
    result,
    onSaveBillingDetails,
    saveBillingDetailsInProgress,
    saveBillingDetailsError,
  } = props;

  const privateData = currentUser?.attributes?.profile?.privateData || {};
  const billing = privateData.billing || billingFromInvoicing(privateData.invoicing);
  const [step, setStep] = useState(needsBillingStep(billing) ? STEP_BILLING : STEP_REQUEST);

  // The panel keeps this modal mounted, so the starting step is re-derived
  // on every open: the profile may have been completed elsewhere meanwhile.
  useEffect(() => {
    if (isOpen) setStep(needsBillingStep(billing) ? STEP_BILLING : STEP_REQUEST);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // The duck doesn't clear a previous result when a new request starts, so a
  // stale result must not hide the in-progress submit (and its spinner) the
  // next time the customer sends a request.
  const showResultView = !!result && !submitInProgress;
  const outcome = showResultView ? OUTCOMES[result.status] || OUTCOMES.pending : null;
  const resultCompany = outcome && result.company && result.company.name ? result.company : null;

  const resultView = outcome ? (
    <div className={css.requestInvoicingResult}>
      <div className={classNames(css.requestInvoicingStatus, outcome.className)} role="status">
        <outcome.Icon className={css.requestInvoicingStatusIcon} aria-hidden="true" />
        <div>
          <p className={css.requestInvoicingStatusHeading}>
            <FormattedMessage id={outcome.statusId} />
          </p>
          <p className={css.requestInvoicingStatusText}>
            <FormattedMessage id={outcome.textId} />
          </p>
          {resultCompany ? (
            <p className={css.requestInvoicingStatusCompany}>
              {resultCompany.name}
              {resultCompany.businessId ? ` · ${resultCompany.businessId}` : ''}
            </p>
          ) : null}
        </div>
      </div>
      <div className={css.modalActionButtons}>
        {result.status === 'approved' ? (
          <PrimaryButton onClick={onClose}>
            <FormattedMessage id="RequestInvoicingModal.continueToOffer" />
          </PrimaryButton>
        ) : (
          <SecondaryButton onClick={onClose}>
            <FormattedMessage id="RequestInvoicingModal.backToOffer" />
          </SecondaryButton>
        )}
      </div>
    </div>
  ) : null;

  // Both form steps lead back to the offer, like the outcome view does: the
  // panel's onClose reopens the offer modal. Unsaved form input is dropped.
  const backToOfferButton = (
    <SecondaryButton type="button" className={css.requestInvoicingBack} onClick={onClose}>
      <FormattedMessage id="RequestInvoicingModal.backToOffer" />
    </SecondaryButton>
  );

  const billingStep = (
    <>
      <p className={css.requestInvoicingStep}>
        <FormattedMessage id="RequestInvoicingModal.stepBilling" />
      </p>
      <p className={css.modalDescriptionText}>
        <FormattedMessage id="RequestInvoicingModal.billingStepIntro" />
      </p>
      <BillingDetailsForm
        compact
        formId="RequestInvoicingModalBilling"
        initialValues={{ ...billingInitialValuesForUser(currentUser), customerType: 'BUSINESS' }}
        inProgress={saveBillingDetailsInProgress}
        saveError={saveBillingDetailsError}
        onSubmit={values =>
          onSaveBillingDetails(values)
            .then(() => setStep(STEP_REQUEST))
            .catch(() => null)
        }
      />
      {backToOfferButton}
    </>
  );

  const requestStep = (
    <>
      <p className={css.requestInvoicingStep}>
        <FormattedMessage id="RequestInvoicingModal.stepRequest" />
      </p>
      <p className={css.modalDescriptionText}>
        <FormattedMessage id="RequestInvoicingModal.intro" />
      </p>
      <RequestInvoicingForm
        formId="RequestInvoicingModal"
        billing={billing}
        currentUser={currentUser}
        onLookupCompany={onLookupCompany}
        lookupInProgress={lookupInProgress}
        lookupResult={lookupResult}
        lookupError={lookupError}
        onSubmit={onSubmit}
        submitInProgress={submitInProgress}
        submitError={submitError}
        onEditBilling={() => setStep(STEP_BILLING)}
      />
      {backToOfferButton}
    </>
  );

  return (
    <Modal
      id="RequestInvoicingModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <div className={css.customOfferModalContent}>
        <h2 className={css.customOfferModalTitle}>
          <FormattedMessage id={outcome ? outcome.titleId : 'RequestInvoicingModal.title'} />
        </h2>
        {resultView || (step === STEP_BILLING ? billingStep : requestStep)}
      </div>
    </Modal>
  );
};

RequestInvoicingModal.defaultProps = {
  currentUser: null,
  lookupInProgress: false,
  lookupResult: null,
  lookupError: null,
  submitInProgress: false,
  submitError: null,
  result: null,
  onSaveBillingDetails: () => Promise.resolve(),
  saveBillingDetailsInProgress: false,
  saveBillingDetailsError: null,
};

RequestInvoicingModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  intl: intlShape.isRequired,
  currentUser: propTypes.currentUser,
  onLookupCompany: func.isRequired,
  lookupInProgress: bool,
  lookupResult: shape({ company: object, revenue: object }),
  lookupError: propTypes.error,
  onSubmit: func.isRequired,
  submitInProgress: bool,
  submitError: propTypes.error,
  result: shape({ status: string }),
  onSaveBillingDetails: func,
  saveBillingDetailsInProgress: bool,
  saveBillingDetailsError: propTypes.error,
};

export default RequestInvoicingModal;
